import { useState } from 'react';

import Button from 'components/atoms/Button';
import Card from 'components/atoms/Card';
import BackButton from 'components/molecules/BackButton';
import TemplateForm from 'pages/_AdminAuthoringPages/_components/AuthoringTemplateForm';
import Typography from 'components/atoms/Typography';
import InAppRoutes, { pushProgressive } from 'utils/router';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { useThunkDispatch } from 'utils/reduxHook';
import { createTemplate } from 'actions/requests';
import TemplatePreviewCard from 'components/organisms/TemplatePreviewCard';
import DiscoverCardPreview from 'pages/_AdminAuthoringPages/_components/DiscoverCardPreview';
import {
  ActionTemplateConfig,
  BroadcastTemplateConfig,
  DevotionalTemplateConfig,
  DownloadTemplateConfig,
  LinkTemplateConfig,
  ScholarshipTemplateConfig,
  TemplateConfig,
  Templates,
  TemplateType,
  VideoTemplateConfig,
} from 'utils/gloo-types';
import { capitalize } from 'lodash';
import { discoverItemCardForTemplate } from 'utils/discover';

function AdminTemplateCreate() {
  const dispatch = useThunkDispatch();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const templateTypeParam = useParams<{ templateType: string }>().templateType.toUpperCase();

  const templateType: TemplateType = TemplateType[templateTypeParam as keyof typeof TemplateType];

  let templateConfig: TemplateConfig;
  switch (templateType) {
    case TemplateType.BROADCAST:
      templateConfig = {} as BroadcastTemplateConfig;
      break;
    case TemplateType.KEYWORD:
      templateConfig = {
        keywords: [],
      } as ActionTemplateConfig;
      break;
    case TemplateType.LINK:
      templateConfig = {
        url: '',
      } as LinkTemplateConfig;
      break;
    case TemplateType.SCHOLARSHIP:
      templateConfig = {
        url: '',
        originalCost: '',
        newCost: '',
      } as ScholarshipTemplateConfig;
      break;
    case TemplateType.VIDEO:
      templateConfig = {
        url: '',
        downloadUrl: '',
        canDownload: true,
      } as VideoTemplateConfig;
      break;
    case TemplateType.SERIES:
      templateConfig = {
        keywords: [],
        content: {
          messages: [],
        },
      } as DevotionalTemplateConfig;
      break;
  }

  const [newTemplate, setNewTemplate] = useState<Partial<Templates>>({
    tags: [],
    type: TemplateType[templateTypeParam],
    config: templateConfig,
  });

  const handleTemplateCreate = async () => {
    setIsSaving(true);
    try {
      if (!newTemplate.shortDescription || !newTemplate.longDescription || !newTemplate.title) {
        toast.info(
          `Please fill out all required fields: ${[
            !newTemplate.shortDescription ? 'Short Description' : undefined,
            !newTemplate.longDescription ? 'Long Description' : undefined,
            !newTemplate.title ? 'Title' : undefined,
          ]
            .filter(Boolean)
            .join(', ')}`,
        );
        return;
      }
      const createdTemplate = await dispatch(createTemplate(newTemplate));
      if (!createdTemplate.error) {
        pushProgressive(InAppRoutes.AdminAuthoringHome);
        toast.success('Template Created');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <article className="container-1">
      <Card border className="flex items-center justify-between p-4 m-2">
        <BackButton
          route={InAppRoutes.AdminAuthoringHome}
          text="Cancel"
          type="tertiary"
          size="small"
          dataDurableId="adminTemplateCreateBackButton"
        />
        <Typography className="text-thryveGreenDark flex-grow" variant="h1">
          Create {capitalize(templateType)} Template
        </Typography>
        <Button
          icon="save"
          iconPosition="left"
          buttonType="submit"
          dataDurableId="adminTemplateCreateSaveButton"
          form="templateCreateForm"
          type="primary"
          text="SAVE"
          disabled={isSaving}
        />
      </Card>
      <section className="grid grid-cols-4">
        <Card border className="p-4 m-2 col-span-3">
          <TemplateForm
            formId="templateCreateForm"
            template={newTemplate}
            handleChange={setNewTemplate}
            handleSubmit={handleTemplateCreate}
          />
        </Card>
        <figure className="col-span-1 p-4">
          <Typography className="text-thryveGray my-4" variant="h3">
            DISCOVER CARD PREVIEW:
          </Typography>
          {newTemplate && (
            <DiscoverCardPreview
              className="w-80"
              itemCard={discoverItemCardForTemplate(newTemplate)}
              onClick={() => {
                if (newTemplate.type === TemplateType.LINK) {
                  const config = newTemplate.config as LinkTemplateConfig;
                  window.open(config.url);
                } else if (newTemplate.type === TemplateType.DOWNLOAD) {
                  const config = newTemplate.config as DownloadTemplateConfig;
                  window.open(config.url);
                }
              }}
            />
          )}
          <Card border className="mt-10">
            <Typography className="text-thryveGray my-4" variant="h3">
              DRAWER HEADER PREVIEW:
            </Typography>
            {newTemplate && <TemplatePreviewCard template={newTemplate} />}
          </Card>
        </figure>
      </section>
    </article>
  );
}

export default AdminTemplateCreate;
